import { addMonths, type YearMonth } from '../../ui';
import type { CategorySlice, DashboardModel, GroupKey, Mover, UpcomingPayment } from './dashboardModel';

/**
 * FICTIONAL sample data for designing Home before real calculations exist.
 * Deterministic per month (seasonal heating, a December spike, rent steady)
 * so switching months shows believable, stable movement. September 2026
 * reproduces the approved Home comp: $2,840, +8.4% vs August.
 *
 * Amounts are in major units of the budget currency here and scaled to
 * minor units on the way out.
 */
interface SubSpec {
  key: string;
  base: number;
  season?: number[]; // 12 factors, Jan..Dec
}

interface CatSpec {
  key: string;
  colorSlot: number;
  group: GroupKey;
  subs: SubSpec[];
}

// Jan..Dec. Winter heating, a going-out summer that ends in September, a December spike.
const HEATING = [1.6, 1.45, 1.15, 0.85, 0.66, 0.55, 0.58, 0.66, 0.9, 1.1, 1.35, 1.55];
const HOLIDAY = [0.8, 0.75, 0.9, 0.95, 1, 1.15, 1.3, 1.2, 1.05, 0.9, 1.15, 1.75];
const SUMMER = [0.8, 0.75, 0.9, 1, 1.1, 1.3, 1.45, 1.5, 1.05, 0.9, 0.95, 1.3];
/** Month-level life events on everything that is not a fixed bill: a quiet April, a July trip, December. */
const EVENTS = [0.95, 0.88, 0.97, 0.78, 0.96, 1.08, 1.32, 1, 1, 0.9, 1.02, 1.3];

const CATEGORIES: CatSpec[] = [
  { key: 'housing', colorSlot: 1, group: 'fixed', subs: [{ key: 'rent', base: 850 }] },
  {
    key: 'obligations',
    colorSlot: 2,
    group: 'obligations',
    subs: [
      { key: 'personal_loan', base: 420 },
      { key: 'bank_fee', base: 34 },
    ],
  },
  {
    key: 'food',
    colorSlot: 3,
    group: 'variable',
    subs: [
      { key: 'groceries', base: 300, season: HOLIDAY },
      { key: 'restaurants', base: 140, season: SUMMER },
      { key: 'coffee', base: 36 },
    ],
  },
  {
    key: 'utilities',
    colorSlot: 4,
    group: 'variable',
    subs: [
      { key: 'electricity', base: 112, season: HEATING },
      { key: 'natural_gas', base: 58, season: HEATING },
      { key: 'water', base: 25 },
      { key: 'internet', base: 60 },
      { key: 'mobile', base: 40 },
    ],
  },
  {
    key: 'shopping',
    colorSlot: 5,
    group: 'variable',
    subs: [
      { key: 'clothing', base: 120, season: HOLIDAY },
      { key: 'electronics', base: 70, season: HOLIDAY },
      { key: 'shopping_home', base: 50 },
    ],
  },
  {
    key: 'transportation',
    colorSlot: 6,
    group: 'variable',
    subs: [
      { key: 'fuel', base: 95 },
      { key: 'public_transport', base: 49 },
    ],
  },
  {
    key: 'entertainment',
    colorSlot: 7,
    group: 'variable',
    subs: [
      { key: 'netflix', base: 15.49 },
      { key: 'spotify', base: 10.99 },
      { key: 'cinema', base: 48, season: SUMMER },
      { key: 'gaming', base: 20 },
    ],
  },
  { key: 'other', colorSlot: 0, group: 'variable', subs: [{ key: 'other', base: 55 }] },
];

/** Stable pseudo-random wobble per (key, month) in [-1, 1]. */
function wobble(key: string, ym: YearMonth): number {
  let h = 2166136261 ^ (ym.year * 31 + ym.month);
  for (const ch of key) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return ((h >>> 0) % 2001) / 1000 - 1;
}

function subAmount(s: SubSpec, ym: YearMonth): number {
  const season = s.season?.[ym.month - 1] ?? 1;
  const fixed = FIXED_KEYS.has(s.key);
  const noise = fixed ? 0 : 0.12 * wobble(s.key, ym);
  // Slow drift up over time (prices creep), anchored at Sep 2026.
  const monthsFromAnchor = (ym.year - 2026) * 12 + (ym.month - 9);
  const drift = fixed ? 1 : 1 + 0.004 * monthsFromAnchor;
  const event = fixed ? 1 : EVENTS[ym.month - 1]!;
  return Math.round(s.base * season * event * (1 + noise) * drift * 100);
}

function rawMonth(ym: YearMonth) {
  return CATEGORIES.map((c) => {
    const subcategories = c.subs.map((s) => ({ key: s.key, total: subAmount(s, ym) }));
    return { spec: c, subcategories, total: subcategories.reduce((n, s) => n + s.total, 0) };
  });
}

function sum(xs: { total: number }[]) {
  return xs.reduce((n, x) => n + x.total, 0);
}

/**
 * Scale a month so its total lands on a target (the comp's $2,840 for
 * Sep 2026 and $2,620 for Aug 2026), keeping every category's proportion.
 */
const ANCHORS: Record<string, number> = { '2026-9': 284000, '2026-8': 262000 };

const FIXED_KEYS = new Set(['rent', 'personal_loan', 'internet', 'mobile', 'netflix', 'spotify', 'public_transport', 'bank_fee']);

function monthData(ym: YearMonth) {
  const raw = rawMonth(ym);
  const target = ANCHORS[`${ym.year}-${ym.month}`];
  if (!target) return raw;
  const fixed = raw.reduce((n, c) => n + c.subcategories.filter((s) => FIXED_KEYS.has(s.key)).reduce((m, s) => m + s.total, 0), 0);
  const f = (target - fixed) / (sum(raw) - fixed);
  const scaled = raw.map((c) => {
    const subcategories = c.subcategories.map((s) => (FIXED_KEYS.has(s.key) ? s : { ...s, total: Math.round(s.total * f) }));
    return { ...c, subcategories, total: sum(subcategories) };
  });
  // Rounding remainder goes to groceries so the total is exact.
  const diff = target - sum(scaled);
  const food = scaled.find((c) => c.spec.key === 'food')!;
  food.subcategories[0]!.total += diff;
  food.total += diff;
  return scaled;
}

export const SAMPLE_LATEST: YearMonth = { year: 2026, month: 9 };
export const SAMPLE_EARLIEST: YearMonth = { year: 2025, month: 10 };

function iso(ym: YearMonth, day: number) {
  return `${ym.year}-${String(ym.month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export function mockDashboard(ym: YearMonth, currency = 'USD'): DashboardModel {
  const now = monthData(ym);
  const prevYm = addMonths(ym, -1);
  const prev = monthData(prevYm);

  const categories: CategorySlice[] = now.map((c, i) => ({
    key: c.spec.key,
    colorSlot: c.spec.colorSlot,
    group: c.spec.group,
    total: c.total,
    previous: prev[i]!.total,
    subcategories: [...c.subcategories].sort((a, b) => b.total - a.total),
  }));

  const ranked: Mover[] = now
    .flatMap((c, i) =>
      c.subcategories.map((s, j) => ({
        key: s.key,
        colorSlot: c.spec.colorSlot,
        from: prev[i]!.subcategories[j]!.total,
        to: s.total,
      })),
    )
    .filter((m) => m.from > 0 && Math.abs(m.to - m.from) >= 500)
    .sort((a, b) => Math.abs(b.to - b.from) - Math.abs(a.to - a.from));
  // "What changed" tells both sides: keep the biggest drop even when rises dominate.
  const movers = ranked.slice(0, 4);
  const biggestDrop = ranked.find((m) => m.to < m.from);
  if (biggestDrop && !movers.includes(biggestDrop)) movers[movers.length - 1] = biggestDrop;

  const trend = Array.from({ length: 12 }, (_, i) => {
    const m = addMonths(ym, i - 11);
    return { key: `${m.year}-${String(m.month).padStart(2, '0')}`, month: m, total: sum(monthData(m)) };
  });

  const next = addMonths(ym, 1);
  const upcoming: UpcomingPayment[] = [
    { key: 'rent', colorSlot: 1, dueDate: iso(next, 1), kind: 'fixed', amount: subAmount({ key: 'rent', base: 850 }, next), lastAmount: null },
    { key: 'personal_loan', colorSlot: 2, dueDate: iso(next, 5), kind: 'fixed', amount: 42000, lastAmount: null },
    { key: 'internet', colorSlot: 4, dueDate: iso(next, 8), kind: 'fixed', amount: 6000, lastAmount: null },
    { key: 'netflix', colorSlot: 7, dueDate: iso(next, 12), kind: 'fixed', amount: 1549, lastAmount: null },
    {
      key: 'electricity',
      colorSlot: 4,
      dueDate: iso(next, 14),
      kind: 'variable',
      amount: null,
      lastAmount: now.find((c) => c.spec.key === 'utilities')!.subcategories.find((s) => s.key === 'electricity')!.total,
    },
  ];

  return {
    month: ym,
    currency,
    total: sum(now),
    previousTotal: sum(prev),
    budget: 350000,
    categories,
    movers,
    trend,
    upcoming,
  };
}
